import { Color, ColorV2 } from "config/colors";
import { FontFamily, FontSize, TypographyV2 } from "config/font";
import styled from "styled-components";
import { DatePickerContainer } from "../saveGig/shift/shiftForm.styled";
import { mediaMax } from "components/designV2/breakpoints";
import { Spacing } from "components/designV2/constants";

export const ArrowSign = styled.span`
  padding: 0 10px;
  margin: 0;
  font-size: ${FontSize.H4};

  &:hover {
    background-color: ${ColorV2.Blue6};
    cursor: pointer;
  }
`;

export const MonthSelectContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
  margin-top: 30px;
`;

export const PeriodNavigatorContainer = styled.div`
  display: flex;
  justify-content: center;
  min-width: 280px;
  margin: 0 auto;
`;

export const IndividualSalaryFilters = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg}px;
  flex-wrap: wrap;
`;

export const IndividualSalaryFilterRow = styled(IndividualSalaryFilters)`
  justify-content: space-between;
  width: 100%;
`;

export const IndividualSalaryFilterStack = styled(IndividualSalaryFilters)`
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.md}px;
  width: 100%;
`;

export const IndividualSalaryFilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg}px;
  flex-wrap: wrap;
`;

export const Cell = styled.div`
  background-color: ${ColorV2.White};
  margin-top: 16px;
  padding: 16px 24px;
  border-radius: 24px;
  display: grid;
  grid-row-gap: 10px;
  grid-column-gap: 20px;
  grid-template:
    "workerContainer divider1"
    "grossSalaryContainer netSalaryContainer taxContainer payrollTaxContainer deductionAmountContainer taxTableContainer"
    "divider2"
    "hoursSalaryContainer"
    "divider3"
    "salaryActionContainer"
    / 1fr auto;

  @media (min-width: 640px) {
    grid-template:
      "workerContainer divider1 grossSalaryContainer taxContainer netSalaryContainer  payrollTaxContainer deductionAmountContainer taxTableContainer divider2 hoursSalaryContainer divider3 salaryActionContainer" auto
      / 1fr auto;
  }

  @media (min-width: 970px) {
    grid-template:
      "workerContainer divider1 grossSalaryContainer taxContainer netSalaryContainer  payrollTaxContainer deductionAmountContainer taxTableContainer divider2 hoursSalaryContainer divider3 salaryActionContainer" auto
      / 3fr 1px 2fr 2fr 2fr 2fr 2fr 2fr 1px 2fr 1px 1fr auto;
  }

  p {
    margin: 0;
  }
`;

export const DividerLine1 = styled.div`
  display: none;
  background-color: ${ColorV2.Blue6};
  grid-area: divider1;

  @media (min-width: 640px) {
    display: unset;
  }
`;

export const DividerLine3 = styled.div`
  display: none;
  background-color: ${ColorV2.Blue6};
  grid-area: divider3;

  @media (min-width: 970px) {
    display: unset;
  }
`;

export const WorkerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px;
  grid-area: workerContainer;
`;

export const GrossSalaryContainer = styled(WorkerContainer)`
  grid-area: grossSalaryContainer;
`;

export const NetSalaryContainer = styled(WorkerContainer)`
  grid-area: netSalaryContainer;
`;

export const HoursContainer = styled(WorkerContainer)`
  grid-area: hoursSalaryContainer;
`;

export const SalaryActionContainer = styled(WorkerContainer)`
  grid-area: salaryActionContainer;
  flex-direction: row;
`;

export const TaxContainer = styled(WorkerContainer)`
  grid-area: taxContainer;
`;

export const PayrollTaxContainer = styled(WorkerContainer)`
  grid-area: payrollTaxContainer;
`;

export const DeductionAmountContainer = styled(WorkerContainer)`
  grid-area: deductionAmountContainer;
`;

export const TaxTableContainer = styled(WorkerContainer)`
  grid-area: taxTableContainer;
`;

export const SalaryNoticeContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px;
`;

export const MonthlySalaryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 2fr) repeat(7, minmax(80px, 1fr)) auto;
  column-gap: 10px;
  align-items: center;
  width: 100%;
`;

export const SumRow = styled(SalaryNoticeContainer)``;

export const SalaryPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px;
`;

type SalaryPeriodCardProps = {
  $variant?: "compact" | "wide" | "filter";
};

type SalaryPeriodCardVariant = NonNullable<SalaryPeriodCardProps["$variant"]>;

interface SalaryPeriodCardVariantStyles {
  minHeight: string;
  padding: string;
  width: string;
}

const salaryPeriodCardVariantStyles: Record<
  SalaryPeriodCardVariant,
  SalaryPeriodCardVariantStyles
> = {
  compact: {
    minHeight: "auto",
    padding: `${Spacing.xl}px ${Spacing.xl}px`,
    width: "fit-content",
  },
  filter: {
    minHeight: "auto",
    padding: `${Spacing.lg}px ${Spacing.lg}px`,
    width: "520px",
  },
  wide: {
    minHeight: "96px",
    padding: `${Spacing.md}px ${Spacing.lg}px`,
    width: "100%",
  },
};

const getSalaryPeriodCardVariantStyles = (
  props: SalaryPeriodCardProps,
): SalaryPeriodCardVariantStyles =>
  salaryPeriodCardVariantStyles[props.$variant ?? "compact"];

export const SalaryPeriodCard = styled.div<SalaryPeriodCardProps>`
  display: inline-flex;
  align-items: center;
  width: ${(props) => getSalaryPeriodCardVariantStyles(props).width};
  max-width: 100%;
  box-sizing: border-box;
  min-height: ${(props) => getSalaryPeriodCardVariantStyles(props).minHeight};
  padding: ${(props) => getSalaryPeriodCardVariantStyles(props).padding};
  border-radius: 24px;
  background: ${ColorV2.White};
  margin-bottom: ${Spacing.lg}px;

  ${mediaMax.tablet} {
    width: 100%;
  }
`;

export const SalaryTabsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md}px;
  flex-wrap: wrap;
  margin-bottom: ${Spacing.md}px;
`;

export const SalaryNoticeActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.md}px;

  ${mediaMax.tablet} {
    align-items: flex-end;
    flex-direction: column;
  }
`;

export const GenerateSalaryNoticesModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

export const GenerateSalaryNoticesOptions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg}px;
  flex-wrap: wrap;
`;

export const GenerateSalaryNoticesWorkerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: ${Spacing.xs}px;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${ColorV2.Blue7};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue6};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue5};
  }
`;

export const GenerateSalaryNoticesWorkerCount = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const GenerateSalaryNoticesWorkerAction = styled.div`
  display: inline-flex;
  align-items: center;
`;

export const SalaryPageBackground = styled.div`
  background-color: ${ColorV2.Blue7};
  min-height: 100vh;
  width: 100%;
  padding-top: 100px;
  padding-bottom: 20px;
  display: flow-root;
`;

type ContainerProps = {
  width?: number;
};

export const Container = styled.div<ContainerProps>`
  position: relative;
  width: ${(props) => (props.width ? `${props.width}px` : `300px`)};
`;

export const Row = styled.div`
  display: grid;
  grid-auto-flow: column;
  width: 100%;
  align-items: center;
`;

export const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid ${Color.Grey};
  border-top: none;
  list-style: none;
  padding: 0;
  margin: 0;
  background: ${ColorV2.White};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

type DropDownProps = {
  isSelected: boolean;
};

export const DropdownItem = styled.li<DropDownProps>`
  padding: 10px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${ColorV2.White};
  }

  ${(props) =>
    props.isSelected &&
    `
    background-color: ${ColorV2.White};
  `}
`;

export const WorkerId = styled.span`
  color: ${Color.DarkGrey};
  font-size: 0.8em;
`;

export const DropDownContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

export const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Logo = styled.img`
  height: 12px;
  width: 40px;
  margin-left: 10px;
  margin-top: 7px;
`;

export const ViewerContainer = styled.div`
  width: 100%;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  background-color: ${ColorV2.White};
  border-radius: 4px;
  overflow: hidden;
`;

export const XmlContent = styled.div`
  flex: 1;
  max-height: 70vh;
  overflow: auto;
  border: 1px solid ${ColorV2.Blue6};
  padding: 10px;
  background-color: ${ColorV2.Blue7};

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: monospace;
  }
`;

export const ViewSwitcherContainer = styled.div`
  margin-left: 70px;
  width: 100%;
  display: flex;
  flex-direction: row;
`;

export const FileViewerContainer = styled.div`
  height: 700px;
  padding: 0px;
  margin: 0px;
`;

export const SalaryEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.md}px;
  padding: ${Spacing.xl}px ${Spacing.md}px;
`;

export const ModalBody = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  height: auto;
  background-color: ${ColorV2.White};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  outline: none;
  border-radius: 8px;
  box-shadow: 0 0 20px 10px rgba(0, 0, 0, 0.2);
  p {
    margin: 0 0 30px 0;
  }
`;

/* Month salaires */

export const MonthSalaryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  margin-top: ${Spacing.md}px;
  margin-bottom: ${Spacing.xl}px;
`;

export const MonthOverviewActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0px;
  justify-content: space-between;
`;

export const FirstContainer = styled(WorkerContainer)`
  grid-area: firstContainer;
`;

export const SecondContainer = styled(WorkerContainer)`
  grid-area: secondContainer;
`;

export const ThirdContainer = styled(WorkerContainer)`
  grid-area: thirdContainer;
`;

type FuturePastSalaryListItemProps = {
  lastRow: boolean;
};

export const FuturePastSalaryListItem = styled.div<FuturePastSalaryListItemProps>`
  background-color: ${ColorV2.White};
  margin: 0 auto;
  width: 95%;
  display: grid;
  padding: 10px;
  grid-row-gap: 10px;
  grid-column-gap: 20px;
  grid-template:
    "firstContainer divider1 secondContainer thirdContainer" 1fr
    / 1.5fr 1px 2fr 2fr auto;

  border-bottom: ${(props) =>
    props.lastRow ? "none" : `1px solid ${ColorV2.Blue6}`};

  @media (min-width: 640px) {
    grid-template:
      "firstContainer divider1 secondContainer thirdContainer" auto
      / 1.5fr 1px 2fr 2fr auto;
  }

  @media (min-width: 970px) {
    grid-template:
      "firstContainer divider1 secondContainer thirdContainer" auto
      / 1.5fr 1px 2fr 2fr auto;
  }

  p {
    margin: 0;
  }
`;

export const FuturePastSalaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px;
`;

type DatePickerProps = {
  focused: boolean;
};

export const SingleDatePickerContainer = styled(
  DatePickerContainer,
)<DatePickerProps>`
  .DateInput_input {
    background-color: ${ColorV2.Blue6};
    border: none;
    font-family: ${FontFamily.MontserratSemiBold};
    color: ${ColorV2.Blue1};
    font-size: 14px;
    text-align: center;
    border-radius: 10px;
    padding: 5px;
    margin-top: 10px;
  }

  #SingleDatePicker {
    ${(props) => (props.focused ? "margin-bottom: 300px" : "10px")};
  }
`;

/* UpdateSalaryContainer */

export const UpdateSalaryContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

export const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: auto;
`;

export const HeaderRightAligned = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

export const SalaryPageBottomMargin = styled(SalaryPageContainer)`
  margin-bottom: 80px;
`;

export const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
`;

export const LoadMoreSpinner = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${Spacing.md}px 0;
`;
